/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import { action } from '@ember/object';
import FormValidations from 'cio/utils/form-validations';
import { isEmpty } from '@ember/utils';
import { Task, task } from 'ember-concurrency';
import { EMPTY_BODY_METHODS, HTTP_VERBS } from 'cio/utils/composer/webhooks';
import ProfileShortcut from 'cio/models/profile-shortcut';
import DS from 'ember-data';
import ApiErrorsService from 'cio/services/api-errors';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { addNewHeader, removeHeader, updateHeaderName, updateHeaderValue } from 'cio/utils/models/header';
import isDestroyed from 'cio/utils/is-destroyed';

interface BaseShortcutModalArgs {
  refreshShortcutsTask: Task<void, []>;
  onClose: () => unknown;
}

interface AddShortcutModalArgs extends BaseShortcutModalArgs {
  mode: 'add';
}

interface EditShortcutModalArgs extends BaseShortcutModalArgs {
  mode: 'edit';
  shortcutId: string;
}

interface DeleteShortcutModalArgs extends BaseShortcutModalArgs {
  mode: 'delete';
  shortcutId: string;
}

type ShortcutModalArgs = AddShortcutModalArgs | EditShortcutModalArgs | DeleteShortcutModalArgs;

enum ProfileShortcutType {
  Link = 'link',
  Webhook = 'webhook',
}

const Validations = {
  url(value: string) {
    if (isEmpty(value?.trim())) {
      return 'must be present';
    }

    return true;
  },

  name(value: string) {
    if (isEmpty(value?.trim())) {
      return 'must be present';
    }

    return true;
  },
};

const MAX_HEADERS_COUNT = 4;

export default class ShortcutModal extends Component<ShortcutModalArgs> {
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service store!: DS.Store;

  @tracked shortcut: ProfileShortcut | null = null;

  shortcutTypes = [
    {
      id: ProfileShortcutType.Link,
      icon: 'link',
      name: 'Link',
    },
    {
      id: ProfileShortcutType.Webhook,
      icon: 'webhook',
      name: 'Webhook',
    },
  ];

  httpVerbs = HTTP_VERBS;

  validations: FormValidations | null = null;

  constructor(owner: unknown, args: ShortcutModalArgs) {
    super(owner, args);

    if (args.mode === 'edit' || args.mode === 'delete') {
      this.fetchShortcut.perform();
    } else if (args.mode === 'add') {
      this.shortcut = this.store.createRecord('profile-shortcut', { webhook: false, requestMethod: 'GET' });
      this.setupValidations();
    }
  }

  @action
  setupValidations() {
    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'shortcut',
    });
  }

  willDestroy() {
    super.willDestroy();

    if (this.shortcut != null && !isDestroyed(this.shortcut)) {
      this.shortcut.rollbackAttributes();
    }
  }

  get shortcutType() {
    if (this.shortcut == null) {
      return ProfileShortcutType.Link;
    }

    return this.shortcut.webhook ? ProfileShortcutType.Webhook : ProfileShortcutType.Link;
  }

  @action
  changeShortcutType(type: ProfileShortcutType) {
    if (this.shortcut == null) {
      return;
    }

    if (type === ProfileShortcutType.Link) {
      this.shortcut.webhook = false;
    } else if (type === ProfileShortcutType.Webhook) {
      this.shortcut.webhook = true;
    }
  }

  get disabled() {
    return (
      !this.auth.hasPermission('journeys/people/edit') || this.saveShortcut.isRunning || this.fetchShortcut.isRunning
    );
  }

  get modalTitle() {
    if (this.args.mode === 'add') {
      return 'Add shortcut';
    }

    if (this.args.mode === 'edit') {
      return 'Edit shortcut';
    }

    if (this.args.mode === 'delete') {
      return 'Delete shortcut';
    }

    return '';
  }

  get isEmptyBodyMethod() {
    if (this.shortcut == null) {
      return false;
    }

    return EMPTY_BODY_METHODS.includes(this.shortcut.requestMethod);
  }

  get hasMaximumHeaders() {
    if (this.shortcut == null) {
      return false;
    }

    return this.shortcut.headers.length >= MAX_HEADERS_COUNT;
  }

  @action
  validate(attribute: string) {
    this.validations?.validateAttribute(attribute);
  }

  fetchShortcut = task({ drop: true }, async () => {
    if (this.args.mode === 'add') {
      return;
    }

    this.shortcut = await this.store.findRecord('profile-shortcut', this.args.shortcutId);
    this.setupValidations();
  });

  saveShortcut = task({ drop: true }, async () => {
    if (this.shortcut == null) {
      return;
    }

    if (this.args.mode === 'add' || this.args.mode === 'edit') {
      try {
        await this.validations?.validateModel();
      } catch (e) {
        // do nothing - validation errors show in the UI
        return;
      }

      this.shortcut.name = this.shortcut.name?.trim();
      this.shortcut.url = this.shortcut.url?.trim();

      try {
        await this.shortcut.save();
      } catch (e) {
        this.apiErrors.report(e);
        throw e;
      }
    } else if (this.args.mode === 'delete') {
      try {
        await this.shortcut.destroyRecord();
      } catch (e) {
        this.apiErrors.report(e);
        throw e;
      }
    }

    await this.args.refreshShortcutsTask.perform();

    this.args.onClose();
  });

  @action
  addHeader() {
    if (this.shortcut == null) {
      return;
    }

    this.shortcut.headers = addNewHeader(this.shortcut.headers);
  }

  @action
  removeHeader(index: number) {
    if (this.shortcut == null) {
      return;
    }

    this.shortcut.headers = removeHeader(this.shortcut.headers, index);
  }

  @action
  updateHeaderName(index: number, event: FlyKeyboardEvent<HTMLInputElement>) {
    if (this.shortcut == null) {
      return;
    }

    this.shortcut.headers = updateHeaderName(this.shortcut.headers, index, event.target.value);
  }

  @action
  updateHeaderValue(index: number, event: FlyKeyboardEvent<HTMLInputElement>) {
    if (this.shortcut == null) {
      return;
    }

    this.shortcut.headers = updateHeaderValue(this.shortcut.headers, index, event.target.value);
  }

  @action
  onClose() {
    if (this.fetchShortcut.isRunning || this.saveShortcut.isRunning) {
      return;
    }

    return this.args.onClose();
  }

  @action
  onShortcutChange(field: keyof ProfileShortcut, value: string) {
    this.shortcut?.set(field, value);
    this.validations?.validateAttribute(field);
  }
}
